import { useState, useEffect, useCallback, useRef } from 'react';
import * as JsSIP from 'jssip';

export interface WebPhoneCredentials {
  username: string; // custid_extension
  password: string;
  extension: string;
  displayName: string;
  sbcServers: Array<{
    host: string;
    wsUri: string;
    priority: number;
  }>;
}

export interface CallSession {
  session: any;
  direction: 'incoming' | 'outgoing';
  remoteNumber: string;
  startTime: Date;
  duration: number;
  status: 'ringing' | 'connected' | 'ended';
}

export interface RTCStats {
  bytesReceived: number;
  bytesSent: number;
  receiveBitrate: number;
  sendBitrate: number;
  packetsReceived: number;
  packetsSent: number;
  packetsLost: number;
  packetsLostPercentage: string;
  jitter: string;
  roundTripTime: string;
  codec: string;
  clockRate: string;
  trackEnabled: boolean;
  trackMuted: boolean;
  trackReadyState: string;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'registered' | 'error';
export type CallStatus = 'idle' | 'calling' | 'ringing' | 'in-call';

// Setup console logging to server file (for debugging)
const setupConsoleLogging = () => {
  const sendLogToServer = (type: string, ...args: any[]) => {
    const timestamp = new Date().toISOString();
    const message = args.map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch (e) {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');

    const logLine = `[${timestamp}] [${type}] ${message}`;

    // Send to server via fetch
    fetch('/api/v1/endpoints/write-log', {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain'
      },
      body: logLine,
      keepalive: true
    }).catch(() => {}); // Ignore errors silently
  };

  // Intercept console methods
  const originalConsoleLog = console.log;
  const originalConsoleWarn = console.warn;
  const originalConsoleError = console.error;
  const originalConsoleInfo = console.info;

  console.log = function(...args: any[]) {
    sendLogToServer('LOG', ...args);
    originalConsoleLog.apply(console, args);
  };
  console.warn = function(...args: any[]) {
    sendLogToServer('WARN', ...args);
    originalConsoleWarn.apply(console, args);
  };
  console.error = function(...args: any[]) {
    sendLogToServer('ERROR', ...args);
    originalConsoleError.apply(console, args);
  };
  console.info = function(...args: any[]) {
    sendLogToServer('INFO', ...args);
    originalConsoleInfo.apply(console, args);
  };
};

export function useWebPhone() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [currentCall, setCurrentCall] = useState<CallSession | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dialedNumber, setDialedNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [audioDevices, setAudioDevices] = useState<{ inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] }>({ inputDevices: [], outputDevices: [] });
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('default');
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('default');
  const [volume, setVolume] = useState<number>(70); // Volume from 0-100, default 70%
  const [rtcStats, setRtcStats] = useState<RTCStats | null>(null);
  const [eventLogs, setEventLogs] = useState<Array<{ timestamp: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }>>([]);
  const [debugMode, setDebugMode] = useState<boolean>(() => {
    // Load debug mode from localStorage
    const saved = localStorage.getItem('webphone_debug_mode');
    return saved === 'true';
  });

  const userAgentRef = useRef<any>(null);
  const currentSessionRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const ringbackAudioContextRef = useRef<AudioContext | null>(null);
  const ringbackOscillatorsRef = useRef<{ osc1: OscillatorNode | null; osc2: OscillatorNode | null }>({ osc1: null, osc2: null });
  const ringbackGainNodeRef = useRef<GainNode | null>(null);
  const sbcHostRef = useRef<string>('sbc15.iatelecom.ca'); // Store current SBC host
  const consoleLoggingSetupRef = useRef<boolean>(false);

  // Toggle debug mode
  const toggleDebugMode = useCallback(() => {
    setDebugMode(prev => {
      const newValue = !prev;
      localStorage.setItem('webphone_debug_mode', String(newValue));

      // Enable/disable JsSIP debug
      if (newValue) {
        if (typeof JsSIP !== 'undefined' && JsSIP.debug) {
          JsSIP.debug.enable('JsSIP:*');
          console.log('[WebPhone] 🐛 Debug mode ENABLED - JsSIP messages will be shown');
        }
      } else {
        if (typeof JsSIP !== 'undefined' && JsSIP.debug) {
          JsSIP.debug.disable();
          console.log('[WebPhone] 🐛 Debug mode DISABLED');
        }
      }

      return newValue;
    });
  }, []);

  // Setup console logging once on mount
  useEffect(() => {
    if (!consoleLoggingSetupRef.current) {
      setupConsoleLogging();
      consoleLoggingSetupRef.current = true;
      console.log('[WebPhone] Console logging to server file enabled');
    }
  }, []);

  // Setup JsSIP debug on mount if debug mode is enabled
  useEffect(() => {
    if (debugMode) {
      if (typeof JsSIP !== 'undefined' && JsSIP.debug) {
        JsSIP.debug.enable('JsSIP:*');
        console.log('[WebPhone] 🐛 Debug mode ENABLED - JsSIP messages will be shown');
      }
    }
  }, [debugMode]);

  // Helper to add event logs (both to console and UI)
  const addLog = useCallback((message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[WebPhone] ${message}`);
    setEventLogs(prev => [...prev.slice(-49), { timestamp, message, type }]); // Keep last 50 logs
  }, []);

  // Start ringback tone (dual-tone US ringback: 440Hz + 480Hz with 2s ring, 4s silence pattern)
  const startRingback = useCallback(() => {
    try {
      // Stop any existing ringback first
      stopRingback();

      console.log('[WebPhone] Starting ringback tone');
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      ringbackAudioContextRef.current = audioContext;

      // Create dual-tone oscillators for US ringback
      const osc1 = audioContext.createOscillator();
      const osc2 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      osc1.frequency.value = 440; // A4
      osc2.frequency.value = 480; // B4
      osc1.type = 'sine';
      osc2.type = 'sine';

      // Connect oscillators to gain node, then to destination
      osc1.connect(gainNode);
      osc2.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Store gain node ref for volume control
      ringbackGainNodeRef.current = gainNode;

      // Create the ring pattern: 2 seconds on, 4 seconds off
      const now = audioContext.currentTime;
      const patternDuration = 6; // Total: 2s ring + 4s silence

      // Calculate gain based on volume setting (0-100 -> 0-0.3 for ringback)
      const ringbackVolume = (volume / 100) * 0.3;

      // Set initial gain to start ringing immediately
      gainNode.gain.setValueAtTime(ringbackVolume, now);

      // Schedule gain changes for the ringback pattern (up to 30 seconds)
      for (let i = 0; i < 5; i++) {
        const cycleStart = now + (i * patternDuration);
        // Ring for 2 seconds
        gainNode.gain.setValueAtTime(ringbackVolume, cycleStart);
        // Silence for 4 seconds
        gainNode.gain.setValueAtTime(0, cycleStart + 2);
      }

      // Start the oscillators after scheduling
      osc1.start(now);
      osc2.start(now);

      ringbackOscillatorsRef.current = { osc1, osc2 };
    } catch (error) {
      console.error('[WebPhone] Failed to start ringback tone:', error);
    }
  }, [volume]);

  // Stop ringback tone
  const stopRingback = useCallback(() => {
    try {
      if (ringbackOscillatorsRef.current.osc1 || ringbackOscillatorsRef.current.osc2) {
        console.log('[WebPhone] Stopping ringback tone');
        ringbackOscillatorsRef.current.osc1?.stop();
        ringbackOscillatorsRef.current.osc2?.stop();
        ringbackOscillatorsRef.current = { osc1: null, osc2: null };
      }

      if (ringbackAudioContextRef.current) {
        ringbackAudioContextRef.current.close();
        ringbackAudioContextRef.current = null;
      }

      ringbackGainNodeRef.current = null;
    } catch (error) {
      // Ignore errors if already stopped
      console.debug('[WebPhone] Error stopping ringback (may already be stopped):', error);
    }
  }, []);

  // Enumerate audio devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Filter and clean up device lists
      const filterDevices = (deviceList: MediaDeviceInfo[]) => {
        // Remove devices without labels (usually means permission not granted)
        const withLabels = deviceList.filter(device => device.label && device.label.trim() !== '');

        // Remove duplicate labels
        const uniqueDevices = withLabels.filter((device, index, self) =>
          index === self.findIndex(d => d.label === device.label)
        );

        return uniqueDevices;
      };

      const inputDevices = filterDevices(devices.filter(device => device.kind === 'audioinput'));
      const outputDevices = filterDevices(devices.filter(device => device.kind === 'audiooutput'));

      setAudioDevices({ inputDevices, outputDevices });
      console.log('[WebPhone] Audio devices:', { inputDevices, outputDevices });
    } catch (error) {
      console.error('[WebPhone] Failed to enumerate devices:', error);
    }
  }, []);

  // Initialize audio elements
  useEffect(() => {
    // Remote audio element
    const audio = new Audio();
    audio.autoplay = true;
    audio.volume = volume / 100; // Set volume from state (0-100 -> 0-1)
    audio.muted = false; // Make sure not muted
    audio.style.display = 'none'; // Hide the element

    // CRITICAL: Append to DOM for audio playback to work in modern browsers
    document.body.appendChild(audio);
    remoteAudioRef.current = audio;

    console.log('[WebPhone] Audio elements initialized and appended to DOM:', {
      autoplay: audio.autoplay,
      volume: audio.volume,
      muted: audio.muted,
      inDOM: document.body.contains(audio)
    });

    // Enumerate devices on mount
    enumerateDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);

    return () => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        // Remove audio element from DOM
        if (document.body.contains(remoteAudioRef.current)) {
          document.body.removeChild(remoteAudioRef.current);
        }
      }
      // Clean up ringback tone
      stopRingback();
      navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
    };
  }, [enumerateDevices, stopRingback]);

  // Update speaker when selection changes
  useEffect(() => {
    if (remoteAudioRef.current && selectedSpeaker && typeof (remoteAudioRef.current as any).setSinkId === 'function') {
      (remoteAudioRef.current as any).setSinkId(selectedSpeaker)
        .then(() => {
          console.log('[WebPhone] Speaker changed to:', selectedSpeaker);
        })
        .catch((error: any) => {
          console.error('[WebPhone] Failed to change speaker:', error);
        });
    }
  }, [selectedSpeaker]);

  // Update volume when it changes
  useEffect(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = volume / 100;
      console.log('[WebPhone] Volume changed to:', volume);
    }
  }, [volume]);

  // Setup session event handlers
  const setupSessionHandlers = useCallback((session: any, direction: 'incoming' | 'outgoing', remoteNumber: string) => {
    console.log('[WebPhone] ========== SETUP SESSION HANDLERS CALLED ==========');
    console.log('[WebPhone] Direction:', direction, 'Number:', remoteNumber);
    addLog('🔧 Setting up session handlers...', 'info');
    console.log('[WebPhone] setupSessionHandlers called for:', direction, remoteNumber);
    console.log('[WebPhone] Session object:', session);
    console.log('[WebPhone] Session connection exists:', !!session.connection);

    const callSession: CallSession = {
      session,
      direction,
      remoteNumber,
      startTime: new Date(),
      duration: 0,
      status: 'ringing',
    };

    setCurrentCall(callSession);
    currentSessionRef.current = session;

    session.on('progress', () => {
      addLog('📞 Call ringing...', 'info');
      setCallStatus('calling');
      setCurrentCall(prev => prev ? { ...prev, status: 'ringing' } : null);
      // Start ringback tone to let user know call is progressing
      startRingback();
    });

    session.on('accepted', () => {
      addLog('✅ Call accepted by remote party', 'success');
      setCallStatus('in-call');
      setCurrentCall(prev => prev ? { ...prev, status: 'connected', startTime: new Date() } : null);
      // Stop ringback when call is answered
      stopRingback();
    });

    session.on('confirmed', () => {
      console.log('[WebPhone] ========== CONFIRMED EVENT FIRED ==========');
      addLog('🔗 Call confirmed - setting up media', 'info');

      // WORKAROUND: Manually set up audio from peer connection
      if (session.connection) {
        const pc = session.connection;
        addLog('🔌 Setting up audio manually from peerconnection', 'info');
        console.log('[WebPhone] Manual peerconnection setup - connectionState:', pc.connectionState);
        console.log('[WebPhone] Manual peerconnection setup - iceConnectionState:', pc.iceConnectionState);

        const receivers = pc.getReceivers();
        console.log('[WebPhone] Remote receivers count:', receivers.length);
        addLog(`🎧 Found ${receivers.length} receivers`, 'info');

        receivers.forEach((receiver: RTCRtpReceiver, index: number) => {
          if (receiver.track) {
            console.log(`[WebPhone] Receiver ${index} - kind: ${receiver.track.kind}, enabled: ${receiver.track.enabled}, muted: ${receiver.track.muted}, readyState: ${receiver.track.readyState}`);

            // Attach audio tracks
            if (receiver.track.kind === 'audio' && remoteAudioRef.current) {
              addLog(`🎧 Audio track found - ${receiver.track.readyState}`, 'success');
              const stream = new MediaStream([receiver.track]);
              remoteAudioRef.current.srcObject = stream;
              addLog('🔊 Audio stream attached to element', 'success');
              console.log('[WebPhone] ✅ Audio stream attached successfully');
              console.log('[WebPhone] Audio element state:', {
                srcObject: !!remoteAudioRef.current.srcObject,
                paused: remoteAudioRef.current.paused,
                volume: remoteAudioRef.current.volume,
                muted: remoteAudioRef.current.muted
              });

              // Set speaker output
              const audioEl = remoteAudioRef.current as any;
              if (typeof audioEl.setSinkId === 'function') {
                audioEl.setSinkId(selectedSpeaker)
                  .then(() => {
                    console.log('[WebPhone] Output device set to:', selectedSpeaker);
                  })
                  .catch((error: any) => {
                    console.error('[WebPhone] Failed to set output device:', error);
                  });
              }

              // Play audio
              remoteAudioRef.current.play()
                .then(() => {
                  addLog('🎵 Audio playback started!', 'success');
                  console.log('[WebPhone] ✅ Audio playing successfully');
                })
                .catch((e: any) => {
                  addLog(`❌ Audio play failed: ${e.message}`, 'error');
                  console.error('[WebPhone] Audio play error:', e);
                });
            }
          }
        });
      } else {
        console.error('[WebPhone] ❌ No peer connection found in session.connection!');
        addLog('❌ No peer connection available', 'error');
      }
    });

    // Handle BYE request (when remote party hangs up)
    session.on('bye', (e: any) => {
      console.log('[WebPhone] BYE received from remote party:', e);
      console.log('[WebPhone] BYE details:', {
        originator: e.originator,
        cause: e.cause,
        request: e.request
      });
    });

    session.on('ended', (e: any) => {
      console.log('[WebPhone] Call ended');
      console.log('[WebPhone] End details:', {
        originator: e?.originator,
        cause: e?.cause,
        message: e?.message
      });
      setCallStatus('idle');
      setCurrentCall(null);
      currentSessionRef.current = null;
      setIsMuted(false);
      setIsOnHold(false);
      setDialedNumber('');
      // Stop ringback if call ended before being answered
      stopRingback();
    });

    session.on('failed', (e: any) => {
      console.error('[WebPhone] Call failed:', e);
      console.error('[WebPhone] Failed event details:', {
        cause: e.cause,
        originator: e.originator,
        status_code: e.message?.status_code,
        reason_phrase: e.message?.reason_phrase,
        headers: e.message?.headers
      });
      setCallStatus('idle');
      setCurrentCall(null);
      currentSessionRef.current = null;
      setErrorMessage(`Call failed: ${e.cause || 'Unknown error'}`);
      setTimeout(() => setErrorMessage(null), 5000);
      // Stop ringback when call fails
      stopRingback();
    });

    // Handle audio stream
    console.log('[WebPhone] Attaching peerconnection event handler...');
    session.on('peerconnection', (e: any) => {
      console.log('[WebPhone] ========== PEERCONNECTION EVENT FIRED ==========');
      addLog('🔌 Peer connection established', 'success');
      const peerconnection = e.peerconnection;

      // Set codec preferences: Opus, G722, PCMU (ULAW)
      try {
        const transceivers = peerconnection.getTransceivers();
        transceivers.forEach((transceiver: RTCRtpTransceiver) => {
          if (transceiver.sender && transceiver.sender.track?.kind === 'audio') {
            const capabilities = RTCRtpSender.getCapabilities('audio');
            if (capabilities && capabilities.codecs) {
              // Order codecs: Opus > G722 > PCMU > PCMA
              const preferredCodecs = capabilities.codecs.filter((codec: RTCRtpCodecCapability) => {
                const mimeType = codec.mimeType.toLowerCase();
                return mimeType === 'audio/opus' ||
                       mimeType === 'audio/g722' ||
                       mimeType === 'audio/pcmu' ||
                       mimeType === 'audio/pcma';
              }).sort((a: RTCRtpCodecCapability, b: RTCRtpCodecCapability) => {
                const order = ['audio/opus', 'audio/g722', 'audio/pcmu', 'audio/pcma'];
                return order.indexOf(a.mimeType.toLowerCase()) - order.indexOf(b.mimeType.toLowerCase());
              });

              if (preferredCodecs.length > 0 && typeof transceiver.setCodecPreferences === 'function') {
                transceiver.setCodecPreferences(preferredCodecs);
                console.log('[WebPhone] Codec preferences set:', preferredCodecs.map((c: RTCRtpCodecCapability) => c.mimeType));
              }
            }
          }
        });
      } catch (error) {
        console.warn('[WebPhone] Could not set codec preferences:', error);
      }

      // Modern API - ontrack
      peerconnection.ontrack = async (event: RTCTrackEvent) => {
        addLog(`🎧 Audio track received (${event.track.readyState})`, 'success');
        console.log('[WebPhone] Remote track received (ontrack):', {
          kind: event.track.kind,
          enabled: event.track.enabled,
          muted: event.track.muted,
          readyState: event.track.readyState,
          streams: event.streams.length
        });

        if (remoteAudioRef.current && event.streams[0]) {
          const stream = event.streams[0];
          remoteAudioRef.current.srcObject = stream;
          addLog('🔊 Audio stream attached to element', 'success');

          // Explicitly play the audio (browsers may block autoplay)
          remoteAudioRef.current.play()
            .then(() => {
              addLog('🎵 Audio playback started!', 'success');
            })
            .catch((error: any) => {
              addLog(`❌ Audio play failed: ${error.message}`, 'error');
            });

          // Ensure output device is set when stream is attached
          const audioEl = remoteAudioRef.current as any;
          if (typeof audioEl.setSinkId === 'function') {
            audioEl.setSinkId(selectedSpeaker)
              .then(() => {
                console.log('[WebPhone] Output device set to:', selectedSpeaker);
              })
              .catch((error: any) => {
                console.error('[WebPhone] Failed to set output device:', error);
              });
          } else {
            console.warn('[WebPhone] setSinkId not supported - cannot select output device');
          }
        }
      };

      // Fallback for older browsers - onaddstream (legacy)
      (peerconnection as any).onaddstream = (event: any) => {
        console.log('[WebPhone] Remote audio stream received (onaddstream - legacy)');
        console.log('[WebPhone] Stream tracks:', event.stream.getTracks().length);

        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.stream;
          console.log('[WebPhone] Remote audio stream attached to element (onaddstream)');

          remoteAudioRef.current.play()
            .then(() => {
              console.log('[WebPhone] Audio playback started successfully (onaddstream)');
            })
            .catch((error: any) => {
              console.error('[WebPhone] Audio playback failed (onaddstream):', error);
            });

          // Ensure output device is set
          const audioEl = remoteAudioRef.current as any;
          if (typeof audioEl.setSinkId === 'function') {
            audioEl.setSinkId(selectedSpeaker).catch((error: any) => {
              console.error('[WebPhone] Failed to set output device (onaddstream):', error);
            });
          }
        }
      };

      // Start RTC Statistics Monitoring (moved outside event handlers)
      let lastBytesReceived = 0;
      let lastBytesSent = 0;
      let lastTimestamp = Date.now();

      const statsInterval = setInterval(async () => {
        try {
          const stats = await peerconnection.getStats();
          const now = Date.now();
          const timeDelta = (now - lastTimestamp) / 1000; // seconds

          // Collect inbound audio stats
          let inboundStats: any = null;
          let outboundStats: any = null;
          let candidatePairStats: any = null;
          let codecStats: any = null;

          stats.forEach((report: any) => {
            if (report.type === 'inbound-rtp' && report.kind === 'audio') {
              inboundStats = report;
            } else if (report.type === 'outbound-rtp' && report.kind === 'audio') {
              outboundStats = report;
            } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
              candidatePairStats = report;
            } else if (report.type === 'codec' && report.mimeType?.includes('audio')) {
              codecStats = report;
            }
          });

          // Calculate bitrates
          const bytesReceivedDelta = inboundStats ? (inboundStats.bytesReceived - lastBytesReceived) : 0;
          const bytesSentDelta = outboundStats ? (outboundStats.bytesSent - lastBytesSent) : 0;
          const receiveBitrate = timeDelta > 0 ? Math.round((bytesReceivedDelta * 8) / timeDelta / 1000) : 0; // kbps
          const sendBitrate = timeDelta > 0 ? Math.round((bytesSentDelta * 8) / timeDelta / 1000) : 0; // kbps

          const packetsLostPercentage = inboundStats && inboundStats.packetsReceived > 0
            ? `${((inboundStats.packetsLost / inboundStats.packetsReceived) * 100).toFixed(2)}%`
            : '0%';

          const jitter = inboundStats?.jitter ? `${(inboundStats.jitter * 1000).toFixed(2)} ms` : 'N/A';
          const roundTripTime = candidatePairStats?.currentRoundTripTime
            ? `${(candidatePairStats.currentRoundTripTime * 1000).toFixed(2)} ms`
            : 'N/A';

          // Update state for UI display
          // Get track info from peerconnection receivers
          let trackInfo = { enabled: false, muted: false, readyState: 'ended' as string };
          const receivers = peerconnection.getReceivers();
          const audioReceiver = receivers.find((r: RTCRtpReceiver) => r.track && r.track.kind === 'audio');
          if (audioReceiver && audioReceiver.track) {
            trackInfo = {
              enabled: audioReceiver.track.enabled,
              muted: audioReceiver.track.muted,
              readyState: audioReceiver.track.readyState
            };
          }

          setRtcStats({
            bytesReceived: inboundStats?.bytesReceived || 0,
            bytesSent: outboundStats?.bytesSent || 0,
            receiveBitrate,
            sendBitrate,
            packetsReceived: inboundStats?.packetsReceived || 0,
            packetsSent: outboundStats?.packetsSent || 0,
            packetsLost: inboundStats?.packetsLost || 0,
            packetsLostPercentage,
            jitter,
            roundTripTime,
            codec: codecStats?.mimeType || 'Unknown',
            clockRate: codecStats?.clockRate?.toString() || 'N/A',
            trackEnabled: trackInfo.enabled,
            trackMuted: trackInfo.muted,
            trackReadyState: trackInfo.readyState
          });

          console.log('[WebPhone] 📊 RTC STATISTICS:', {
            '🔊 Audio Stream': {
              bytesReceived: inboundStats?.bytesReceived || 0,
              bytesSent: outboundStats?.bytesSent || 0,
              receiveBitrate: `${receiveBitrate} kbps`,
              sendBitrate: `${sendBitrate} kbps`,
              packetsReceived: inboundStats?.packetsReceived || 0,
              packetsSent: outboundStats?.packetsSent || 0,
              packetsLost: inboundStats?.packetsLost || 0,
              packetsLostPercentage
            },
            '📡 Network Quality': {
              jitter,
              roundTripTime
            },
            '🎵 Codec': {
              inboundCodec: codecStats?.mimeType || 'Unknown',
              clockRate: codecStats?.clockRate || 'N/A'
            },
            '🔍 Track State': {
              enabled: trackInfo.enabled,
              muted: trackInfo.muted,
              readyState: trackInfo.readyState
            }
          });

          // Update last values for next calculation
          if (inboundStats) lastBytesReceived = inboundStats.bytesReceived;
          if (outboundStats) lastBytesSent = outboundStats.bytesSent;
          lastTimestamp = now;

          // Alert if no audio is being received
          if (inboundStats && inboundStats.bytesReceived === 0 && timeDelta > 3) {
            console.warn('[WebPhone] ⚠️  WARNING: No audio bytes received yet!');
          }

          // Alert if high packet loss
          if (inboundStats && inboundStats.packetsLost > 0) {
            const lossRate = (inboundStats.packetsLost / inboundStats.packetsReceived) * 100;
            if (lossRate > 5) {
              console.warn(`[WebPhone] ⚠️  WARNING: High packet loss detected: ${lossRate.toFixed(2)}%`);
            }
          }

            } catch (error) {
          console.error('[WebPhone] Failed to get RTC stats:', error);
            }
          }, 2000); // Every 2 seconds

          // Start diagnostic monitoring
          const diagnosticInterval = setInterval(() => {
            if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
          const currentStream = remoteAudioRef.current.srcObject as MediaStream;
          const audioEl = remoteAudioRef.current as any;
          console.log('[WebPhone] Audio status check:', {
            streamActive: currentStream.active,
            tracks: currentStream.getTracks().map(t => ({
              kind: t.kind,
              enabled: t.enabled,
              muted: t.muted,
              readyState: t.readyState
            })),
            audioElement: {
              volume: remoteAudioRef.current.volume,
              muted: remoteAudioRef.current.muted,
              paused: remoteAudioRef.current.paused,
              currentTime: remoteAudioRef.current.currentTime,
              duration: remoteAudioRef.current.duration,
              readyState: remoteAudioRef.current.readyState,
              networkState: remoteAudioRef.current.networkState,
              sinkId: audioEl.sinkId || 'default',
              autoplay: remoteAudioRef.current.autoplay
            }
          });
            } else {
          clearInterval(diagnosticInterval);
            }
          }, 3000);

          // Clear diagnostics and stats when call ends
          session.on('ended', () => {
            clearInterval(diagnosticInterval);
            clearInterval(statsInterval);
            setRtcStats(null); // Clear stats display
          });
    });

    // Handle hold/unhold events
    session.on('hold', (e: any) => {
      console.log('[WebPhone] Call put on hold', e);
      setIsOnHold(true);
    });

    session.on('unhold', (e: any) => {
      console.log('[WebPhone] Call resumed from hold', e);
      setIsOnHold(false);
    });

    console.log('[WebPhone] ✅ All session handlers attached successfully');
    addLog('✅ Session handlers ready', 'success');
  }, [startRingback, stopRingback, addLog, selectedSpeaker]);

  // Connect to WebRTC
  const connect = useCallback(async (credentials: WebPhoneCredentials) => {
    try {
      setConnectionStatus('connecting');
      setErrorMessage(null);

      // Request microphone permission early to get device labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        // Enumerate devices again after permission granted
        await enumerateDevices();
      } catch (error) {
        console.warn('[WebPhone] Microphone permission not granted yet:', error);
      }

      // Try primary SBC first
      const primarySBC = credentials.sbcServers.find(s => s.priority === 1) || credentials.sbcServers[0];

      // Store SBC host for use in makeCall
      sbcHostRef.current = primarySBC.host;

      const socket = new JsSIP.WebSocketInterface(primarySBC.wsUri);

      // Simple configuration matching working HTML implementation
      const configuration = {
        sockets: [socket],
        uri: `sip:${credentials.username}@${primarySBC.host}`,
        password: credentials.password,
        display_name: credentials.username,
        register: true,
        session_timers: false,
        register_expires: 600
      };

      console.log('[WebPhone] JsSIP Configuration:', {
        uri: configuration.uri,
        display_name: configuration.display_name,
        wsUri: primarySBC.wsUri
      });

      const userAgent = new JsSIP.UA(configuration);
      userAgentRef.current = userAgent;

      // Event handlers
      userAgent.on('connected', () => {
        console.log('[WebPhone] WebSocket connected');
        setConnectionStatus('connected');
      });

      userAgent.on('disconnected', () => {
        console.log('[WebPhone] WebSocket disconnected');
        setConnectionStatus('disconnected');
      });

      userAgent.on('registered', () => {
        console.log('✅✅✅ [WebPhone] ========================================');
        console.log('✅✅✅ [WebPhone] SUCCESSFULLY REGISTERED!');
        console.log('✅✅✅ [WebPhone] User:', credentials.username);
        console.log('✅✅✅ [WebPhone] Server:', primarySBC.host);
        console.log('✅✅✅ [WebPhone] You can now make calls!');
        console.log('✅✅✅ [WebPhone] ========================================');
        setConnectionStatus('registered');
        setErrorMessage(null); // Clear any previous errors

        // Show a temporary success message
        setErrorMessage('✅ Successfully registered! Ready to make calls.');
        setTimeout(() => setErrorMessage(null), 3000);
      });

      userAgent.on('unregistered', (e: any) => {
        console.log('[WebPhone] SIP unregistered:', e);
        setConnectionStatus('disconnected');
      });

      userAgent.on('registrationFailed', (e: any) => {
        console.error('[WebPhone] Registration failed:', e);
        console.error('[WebPhone] Registration failure details:', {
          cause: e.cause,
          response: e.response,
          status_code: e.response?.status_code,
          reason_phrase: e.response?.reason_phrase,
        });

        // If it's a temporary error (503, connection error, timeout), keep retrying
        const isTemporaryError =
          e.cause === 'Connection Error' ||
          e.cause === 'Request Timeout' ||
          e.response?.status_code === 503 ||
          !e.response;

        if (isTemporaryError) {
          console.log('[WebPhone] Temporary error detected, will retry in 2 seconds...');
          // Don't set error status for temporary failures - JsSIP will auto-retry
          // Just keep the current status and show a warning
          setErrorMessage(`Connecting... (${e.response?.reason_phrase || e.cause})`);

          setTimeout(() => {
            console.log('[WebPhone] Retrying registration...');
            if (userAgentRef.current && userAgentRef.current.isConnected()) {
              userAgentRef.current.register();
            }
          }, 2000);
        } else {
          // Auth failures or other permanent errors
          setConnectionStatus('error');
          const errorMsg = e.response?.reason_phrase || e.cause || 'Unknown error';
          setErrorMessage(`Registration failed: ${errorMsg}`);
        }
      });

      userAgent.on('newRTCSession', (data: any) => {
        const session = data.session;

        if (session.direction === 'incoming') {
          // Incoming call
          console.log('[WebPhone] Incoming call from:', session.remote_identity.uri.user);
          const remoteNumber = session.remote_identity.uri.user;

          setCallStatus('ringing');
          setupSessionHandlers(session, 'incoming', remoteNumber);

          // Auto-answer could be added here or show notification
          // For now, we'll need to call answerCall() manually
        }
      });

      // Wait for WebSocket to be fully connected before starting
      userAgent.on('connected', () => {
        console.log('[WebPhone] WebSocket connected, ready for registration');
      });

      userAgent.start();
    } catch (error) {
      console.error('[WebPhone] Connect error:', error);
      setConnectionStatus('error');
      setErrorMessage(`Connection error: ${error}`);
    }
  }, [setupSessionHandlers, enumerateDevices]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.terminate();
    }
    if (userAgentRef.current) {
      userAgentRef.current.stop();
    }
    setConnectionStatus('disconnected');
    setCallStatus('idle');
    setCurrentCall(null);
    setDialedNumber('');
  }, []);

  // Make a call
  const makeCall = useCallback(async (number: string) => {
    if (!userAgentRef.current || connectionStatus !== 'registered') {
      setErrorMessage('Not connected. Please wait for registration.');
      addLog('Cannot call: Not registered', 'error');
      return;
    }

    if (!number || number.trim() === '') {
      setErrorMessage('Please enter a number');
      addLog('Cannot call: No number entered', 'error');
      return;
    }

    // Prevent multiple simultaneous calls
    if (callStatus !== 'idle') {
      addLog('Call already in progress, ignoring duplicate request', 'warning');
      return;
    }

    // IMMEDIATELY set status to calling to disable button
    addLog('📞 Initiating call...', 'info');
    setCallStatus('calling');

    try {
      // Prime the audio element during user interaction to establish autoplay permission
      // This must happen during the user click to satisfy browser autoplay policies
      if (remoteAudioRef.current) {
        console.log('[WebPhone] Priming audio element for autoplay...');
        // Don't await - make this non-blocking so call proceeds even if it fails
        remoteAudioRef.current.play().then(() => {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.pause();
            remoteAudioRef.current.currentTime = 0;
          }
          console.log('[WebPhone] Audio element primed successfully');
        }).catch((error) => {
          // Expected to fail if no source - that's OK, permission is still established
          console.log('[WebPhone] Audio priming info:', error.message || error);
        });
      }

      // Request microphone permission first
      addLog('🎤 Requesting microphone permission...', 'info');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      addLog('✅ Microphone permission granted', 'success');

      // Stop the test stream - JsSIP will create its own
      stream.getTracks().forEach(track => track.stop());

      const eventHandlers = {
        progress: () => addLog('📞 Call in progress (ringing)', 'info'),
        failed: (e: any) => addLog(`❌ Call failed: ${e.cause}`, 'error'),
        ended: (e: any) => addLog(`📴 Call ended: ${e.cause}`, 'warning'),
        accepted: () => addLog('✅ Call accepted', 'success'),
        sdp: (e: any) => {
          console.log('======== SDP EVENT ========');
          console.log('Originator:', e.originator);
          console.log('Type:', e.type);
          console.log('SDP:\n' + e.sdp);
          console.log('===========================');

          // Log codec info from SDP
          if (e.originator === 'remote') {
            addLog('REMOTE SDP RECEIVED', 'info');
            const audioMatch = e.sdp.match(/m=audio (\d+) [^\r\n]+/);
            if (audioMatch) {
              addLog('Audio line: ' + audioMatch[0], 'info');
            }
            const codecMatches = e.sdp.match(/a=rtpmap:\d+ [^\r\n]+/g);
            if (codecMatches) {
              codecMatches.forEach((codec: string) => {
                addLog('Codec: ' + codec, 'info');
              });
            }
          }

          // Only modify local SDP (our outgoing offer)
          if (e.originator === 'local') {
            addLog('🔧 Modifying local SDP for FreeSWITCH compatibility', 'info');
            e.sdp = e.sdp.replace(/a=setup:actpass/g, 'a=setup:active');
            addLog('✅ Changed setup:actpass to setup:active', 'success');
          }
        }
      };

      const options = {
        eventHandlers,
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302'] }
          ]
        },
        rtcOfferConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        },
        rtcAnswerConstraints: {
          offerToReceiveAudio: true,
          offerToReceiveVideo: false
        }
      };

      // Use the stored SBC host from connection
      const sipUri = `sip:${number}@${sbcHostRef.current}`;
      addLog(`📡 Sending INVITE to ${sipUri}`, 'info');

      console.log('[WebPhone] About to call ua.call()...');
      const session = userAgentRef.current.call(sipUri, options);
      console.log('[WebPhone] ua.call() returned, session:', session);
      console.log('[WebPhone] About to call setupSessionHandlers()...');
      setupSessionHandlers(session, 'outgoing', number);
      console.log('[WebPhone] setupSessionHandlers() completed');
    } catch (error: any) {
      addLog(`❌ Microphone error: ${error.message}`, 'error');
      setErrorMessage(`Microphone access denied: ${error.message}`);
      setCallStatus('idle'); // Reset status on error
      setTimeout(() => setErrorMessage(null), 5000);
    }
  }, [connectionStatus, setupSessionHandlers, callStatus, addLog]);

  // Answer incoming call
  const answerCall = useCallback(() => {
    if (currentSessionRef.current && currentCall?.direction === 'incoming') {
      const eventHandlers = {
        sdp: (e: any) => {
          console.log('======== SDP EVENT (ANSWER) ========');
          console.log('Originator:', e.originator);
          console.log('Type:', e.type);
          console.log('SDP:\n' + e.sdp);
          console.log('====================================');

          // Log codec info from SDP
          if (e.originator === 'remote') {
            addLog('REMOTE SDP RECEIVED (incoming call)', 'info');
            const audioMatch = e.sdp.match(/m=audio (\d+) [^\r\n]+/);
            if (audioMatch) {
              addLog('Audio line: ' + audioMatch[0], 'info');
            }
            const codecMatches = e.sdp.match(/a=rtpmap:\d+ [^\r\n]+/g);
            if (codecMatches) {
              codecMatches.forEach((codec: string) => {
                addLog('Codec: ' + codec, 'info');
              });
            }
          }

          // Only modify local SDP (our outgoing answer)
          if (e.originator === 'local') {
            addLog('🔧 Answer - Modifying local SDP for FreeSWITCH compatibility', 'info');
            e.sdp = e.sdp.replace(/a=setup:actpass/g, 'a=setup:active');
            addLog('✅ Answer - Changed setup:actpass to setup:active', 'success');
          }
        }
      };

      currentSessionRef.current.answer({
        mediaConstraints: { audio: true, video: false },
        eventHandlers,
      });
    }
  }, [currentCall, addLog]);

  // Hang up
  const hangUp = useCallback(() => {
    if (currentSessionRef.current) {
      currentSessionRef.current.terminate();
    }
  }, []);

  // Mute/unmute
  const toggleMute = useCallback(() => {
    if (currentSessionRef.current && currentSessionRef.current.isEstablished()) {
      if (isMuted) {
        currentSessionRef.current.unmute();
      } else {
        currentSessionRef.current.mute();
      }
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // Hold/unhold
  const toggleHold = useCallback(() => {
    if (currentSessionRef.current && currentSessionRef.current.isEstablished()) {
      try {
        if (isOnHold) {
          console.log('[WebPhone] Unholding call...');
          currentSessionRef.current.unhold();
        } else {
          console.log('[WebPhone] Holding call...');
          currentSessionRef.current.hold();
        }
        // State will be updated by 'hold' and 'unhold' event handlers
      } catch (error) {
        console.error('[WebPhone] Hold/unhold failed:', error);
        setErrorMessage(`Hold operation failed: ${error}`);
        setTimeout(() => setErrorMessage(null), 3000);
      }
    }
  }, [isOnHold]);

  // Send DTMF
  const sendDTMF = useCallback((digit: string) => {
    if (currentSessionRef.current && currentSessionRef.current.isEstablished()) {
      currentSessionRef.current.sendDTMF(digit);
    }
    // Also update dialed number during call
    if (callStatus === 'in-call') {
      setDialedNumber(prev => prev + digit);
    }
  }, [callStatus]);

  // Add digit to dialed number
  const addDigit = useCallback((digit: string) => {
    setDialedNumber(prev => prev + digit);
    // If in call, also send as DTMF
    if (callStatus === 'in-call') {
      sendDTMF(digit);
    }
  }, [callStatus, sendDTMF]);

  // Clear dialed number
  const clearNumber = useCallback(() => {
    setDialedNumber('');
  }, []);

  // Change speaker device
  const changeSpeaker = useCallback((deviceId: string) => {
    setSelectedSpeaker(deviceId);
  }, []);

  // Change microphone device
  const changeMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophone(deviceId);
    // Note: Microphone change will take effect on next call
    // For active calls, we would need to renegotiate the stream
  }, []);

  // Test speaker by playing a tone
  const testSpeaker = useCallback(async () => {
    try {
      // Create an audio context and generate a test tone
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Set up a 440Hz tone (A4 note) for 0.5 seconds
      oscillator.frequency.value = 440;
      oscillator.type = 'sine';
      gainNode.gain.value = volume / 100 * 0.3; // Apply current volume setting

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      console.log('[WebPhone] Test tone played');

      // Clean up
      setTimeout(() => {
        audioContext.close();
      }, 600);
    } catch (error) {
      console.error('[WebPhone] Failed to play test tone:', error);
    }
  }, [volume]);

  // Change volume
  const changeVolume = useCallback((newVolume: number) => {
    setVolume(newVolume);
  }, []);

  // Update call duration
  useEffect(() => {
    if (currentCall && currentCall.status === 'connected') {
      const interval = setInterval(() => {
        setCurrentCall(prev => {
          if (!prev || prev.status !== 'connected') return prev;
          const duration = Math.floor((new Date().getTime() - prev.startTime.getTime()) / 1000);
          return { ...prev, duration };
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentCall?.status]);

  return {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    audioDevices,
    selectedSpeaker,
    selectedMicrophone,
    volume,
    rtcStats,
    eventLogs,
    debugMode,
    connect,
    disconnect,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    sendDTMF,
    addDigit,
    clearNumber,
    changeSpeaker,
    changeMicrophone,
    changeVolume,
    enumerateDevices,
    testSpeaker,
    toggleDebugMode,
  };
}
